import { UseToastOptions } from "@chakra-ui/react"
import {
  EntityId,
  PageEntity,
  PrintServiceProductEntity,
  PrintServiceProductEntityHydrated,
} from "@jackfruit/common"
import {
  call,
  delay,
  put,
  race,
  take,
  takeEvery,
} from "@redux-saga/core/effects"
import { SagaIterator } from "@redux-saga/types"
import { PayloadAction } from "@reduxjs/toolkit"
import { t } from "i18next"
import { Channel, channel } from "redux-saga"
import { fork, spawn } from "redux-saga/effects"
import { UPLOAD_ACCEPTED_FILE_TYPES_IMAGES } from "~/data/const"
import { CartEntity } from "~/interfaces/entities/Cart"
import { DocumentAnalysis } from "~/interfaces/entities/DocumentAnalysis"
import { PageSessionEntity } from "~/interfaces/entities/PageSession"
import { getToaster, getTranslator } from "~/redux/context"
import { createUploadFromDocumentFile } from "~/redux/saga/processUpdateDocument"
import { lineItems } from "~/redux/state/lineItems"
import { logger } from "~/services/Logger"
import { getPagesFromProduct } from "~/services/PrintServiceProductHelpers"
import { scrollTo } from "~/services/Utils"
import {
  actions,
  CreateUploadFromButtonPayload,
  CreateUploadFromBuyNowButtonPayload,
  CreateUploadPayload,
  UploadUriImagesPayload,
} from "../process"
import { getCart, getCurrentCart, removeLineItemFromCart } from "./cart"
import { convertImageToJpegIFNecessary, createUpload } from "./images"
import {
  createLineItem,
  updateLineItemFromUpload,
  updateLineItemFromUploadWithProducts,
  updateLineItemFromUploadWithSingleProduct,
} from "./lineItems"
import { getPage } from "./page"
import { attachUploadToPageSession, getPageSession } from "./pageSession"
import {
  getPageProductsForFulfillment,
  rehydratePrintServiceProduct,
} from "./printServiceProducts"
import { updateFulfillment } from "./processUpdateFulfillment"
import { duplicateUpload, waitForUploadReady } from "./uploads"

const PROCESS_WORKERS_COUNT = 4
const CREATE_UPLOAD_DELAY = 1000

export function* watchCreateUpload(): SagaIterator {
  yield call(processCreateUploadType, actions.createUpload.type)
}

export function* watchCreateUploadFromButton(): SagaIterator {
  yield call(processCreateUploadType, actions.createUploadFromButton.type)
}

export function* watchCreateUploadFromBuyNowButton(): SagaIterator {
  yield call(processCreateUploadType, actions.createUploadFromBuyNowButton.type)
}

function* processCreateUploadType(actionType: string): SagaIterator {
  const chan = yield call(channel)

  for (let i = 0; i < PROCESS_WORKERS_COUNT; i++) {
    // Determine which process to fork based on actionType
    let process = undefined
    if (actionType === actions.createUploadFromButton.type) {
      process = processCreateUploadFromButton
    } else if (actionType === actions.createUploadFromBuyNowButton.type) {
      process = processCreateUploadFromBuyNowButton
    } else {
      process = processCreateUpload
    }

    yield fork(process, chan)
  }

  while (true) {
    const { payload } = yield take(actionType)

    const pageSession: PageSessionEntity = yield call(getPageSession, {
      pageSessionId: payload.pageId,
    })
    const cart: CartEntity = yield call(getCart, pageSession.cartId)

    const lineItemId = yield call(createLineItem)

    yield put(actions.addLineItemToCart({ lineItemId, cartId: cart.id }))

    yield put(chan, { ...payload, lineItemId })
  }
}

function* processCreateUpload(
  chan: Channel<CreateUploadPayload & { lineItemId: EntityId }>,
): SagaIterator {
  while (true) {
    const payload: CreateUploadPayload & { lineItemId: EntityId } =
      yield take(chan)
    yield delay(CREATE_UPLOAD_DELAY)

    const { file } = payload

    if (file.type.startsWith("image/")) {
      yield call(processCreateUploadFromImageFile, payload)
    } else {
      yield call(processCreateUploadFromDocumentFile, payload)
    }
  }
}

function* processCreateUploadFromImageFile(
  payload: CreateUploadPayload & { lineItemId: EntityId },
): SagaIterator {
  const { pageId, file, scrollTo, nextBlockName, lineItemId } = payload
  try {
    const convertedFile = yield call(convertImageToJpegIFNecessary, file)

    // create upload
    const { uploadId } = yield call(createUpload, { file: convertedFile })
    yield call(attachUploadToPageSession, uploadId)

    // queue upload for processing
    yield put(
      actions.queueImageForProcessing({
        id: uploadId,
        file: convertedFile,
      }),
    )

    if (scrollTo && nextBlockName) {
      scrollTo(nextBlockName)
    }

    yield yield delay(CREATE_UPLOAD_DELAY)

    yield spawn(applyFlowForUploadSafely, { pageId, uploadId, lineItemId })
  } catch (error) {
    logger.error(error)
  }
}

function* processCreateUploadFromDocumentFile(
  payload: CreateUploadPayload & { lineItemId: EntityId },
): SagaIterator {
  const { file, pageId, lineItemId, nextBlockName, scrollTo, productId } =
    payload
  const cart = yield call(getCurrentCart)

  const { updateOne: updateOneLineItem } = lineItems.actions

  try {
    if (scrollTo && nextBlockName) {
      scrollTo(nextBlockName)
    }

    const cart: CartEntity = yield call(getCurrentCart)

    const { fulfillment } = cart

    const availableProducts: PrintServiceProductEntity[] = yield call(
      getPageProductsForFulfillment,
      {
        pageId,
        fulfillment,
      },
    )

    let firstDocumentProduct: PrintServiceProductEntity | undefined
    if (productId) {
      firstDocumentProduct = availableProducts.find(
        product => product.id === productId,
      )
    } else {
      firstDocumentProduct = availableProducts.find(
        product => product.metaData?.document?.enabled,
      )
    }

    if (!firstDocumentProduct) {
      const errorMessage =
        "No document product available for this page and fulfillment"
      logger.error(errorMessage, { fulfillment, pageId })

      throw new Error(errorMessage)
    }

    const {
      uploadId,
      documentAnalysis,
    }: {
      uploadId: EntityId
      documentAnalysis: DocumentAnalysis
    } = yield call(() => createUploadFromDocumentFile({ file }))

    yield put(
      updateOneLineItem({
        id: lineItemId,
        changes: {
          quantity: 1,
          orientation: "portrait",
          documentUploadId: uploadId,
          documentPageCount: documentAnalysis.pageCount,
          productPageIds: [],
          productId: firstDocumentProduct.id,
          renderedUploadIds: [],
          isReady: true,
          isDocument: true,
        },
      }),
    )

    yield call(attachUploadToPageSession, uploadId)

    yield call(applyFlowForUpload, { pageId, lineItemId })

    yield put(
      updateOneLineItem({
        id: lineItemId,
        changes: { isConfirmed: true },
      }),
    )
  } catch (error) {
    yield call(removeLineItemFromCart, { lineItemId, cartId: cart.id })

    logger.error(error)
    const toaster = getToaster()
    const t = getTranslator()

    toaster({
      title: t(
        "redux.saga.processUploads.processCreateUploadFromDocumentFile.error.title",
      ),
      description: t(
        "redux.saga.processUploads.processCreateUploadFromDocumentFile.error.description",
      ),
      status: "error",
      duration: 9000,
      isClosable: true,
    })
  }
}

function* processCreateUploadFromBuyNowButton(
  chan: Channel<CreateUploadFromBuyNowButtonPayload & { lineItemId: EntityId }>,
): SagaIterator {
  while (true) {
    const payload = yield take(chan)
    yield delay(CREATE_UPLOAD_DELAY)

    const { pageId, file, lineItemId, productId, fulfillment } = payload

    try {
      const page: PageEntity = yield call(getPage, { pageId })
      const pageSession: PageSessionEntity = yield call(getPageSession, {
        pageSessionId: pageId,
      })
      const cart: CartEntity = yield call(getCart, pageSession.cartId)
      const { fulfillment: cartFulfillment } = cart

      if (cartFulfillment !== fulfillment) {
        yield call(updateFulfillment, { pageId, fulfillment })
      }

      const convertedFile = yield call(convertImageToJpegIFNecessary, file)

      // create upload for each image regions
      const printServiceProductEntityHydrated: PrintServiceProductEntityHydrated =
        yield call(rehydratePrintServiceProduct, productId)

      const requiredProductPages = getPagesFromProduct(
        printServiceProductEntityHydrated,
      )

      const allImageRegions = requiredProductPages
        .map(page => {
          return page.imageRegions
        })
        .flat()

      const allUploadIds: EntityId[] = []
      const { uploadId } = yield call(createUpload, { file: convertedFile })
      yield call(attachUploadToPageSession, uploadId)
      yield put(
        actions.queueImageForProcessing({
          id: uploadId,
          file: convertedFile,
        }),
      )

      yield call(waitForUploadReady, uploadId)

      for (const _imageRegion of allImageRegions) {
        const duplicatedUpload = yield call(duplicateUpload, uploadId)
        allUploadIds.push(duplicatedUpload.id)
      }

      // store first pickup has special needs
      if (
        cart.fulfillment === "pickup" &&
        pageSession.pageFlow === "store-first" &&
        !pageSession.hasLoadedStores
      ) {
        scrollTo("store-search")
        // pause until a store has been selected
        // or fulfillment has been changed
        yield race([
          take(actions.updatedStoreForPage(page.id).type),
          take(actions.updatedFulfillmentForPage(page.id).type),
        ])
      } else {
        scrollTo("cart")
      }

      yield call(updateLineItemFromUploadWithSingleProduct, {
        lineItemId,
        uploadIds: allUploadIds,
        productId,
      })

      yield put(
        actions.updateOrderSummary({
          reason: "created line item after upload",
          reasonType: "cartChange",
        }),
      )
    } catch (error) {
      logger.error(error)
    }
  }
}

function* processCreateUploadFromButton(
  chan: Channel<CreateUploadFromButtonPayload & { lineItemId: EntityId }>,
): SagaIterator {
  while (true) {
    const payload = yield take(chan)
    yield delay(CREATE_UPLOAD_DELAY)

    const {
      pageId,
      file,
      lineItemId,
      productIds = [],
      productCategoryIds = [],
    } = payload

    try {
      const page: PageEntity = yield call(getPage, { pageId })
      const pageSession: PageSessionEntity = yield call(getPageSession, {
        pageSessionId: pageId,
      })
      const cart: CartEntity = yield call(getCart, pageSession.cartId)

      const convertedFile = yield call(convertImageToJpegIFNecessary, file)

      // create upload
      const { uploadId } = yield call(createUpload, { file: convertedFile })
      yield call(attachUploadToPageSession, uploadId)

      // queue upload for processing
      yield put(
        actions.queueImageForProcessing({
          id: uploadId,
          file: convertedFile,
        }),
      )

      // store first pickup has special needs
      if (
        cart.fulfillment === "pickup" &&
        pageSession.pageFlow === "store-first" &&
        !pageSession.hasLoadedStores
      ) {
        scrollTo("store-search")
        // pause until a store has been selected
        // or fulfillment has been changed
        yield race([
          take(actions.updatedStoreForPage(page.id).type),
          take(actions.updatedFulfillmentForPage(page.id).type),
        ])
      } else {
        scrollTo("cart")
      }

      yield call(updateLineItemFromUploadWithProducts, {
        lineItemId,
        uploadId,
        productIds,
        productCategoryIds,
      })

      yield put(
        actions.updateOrderSummary({
          reason: "created line item after upload",
          reasonType: "cartChange",
        }),
      )
    } catch (error) {
      logger.error(error)
    }
  }
}

function* applyFlowForUploadSafely(payload: {
  pageId: EntityId
  uploadId?: EntityId
  lineItemId: EntityId
}): SagaIterator {
  try {
    yield call(applyFlowForUpload, payload)
  } catch (error) {
    logger.error(error)
  }
}

export function* applyFlowForUpload(payload: {
  pageId: EntityId
  uploadId?: EntityId
  lineItemId: EntityId
}): SagaIterator<EntityId> {
  const { pageId, uploadId, lineItemId } = payload
  const page: PageEntity = yield call(getPage, { pageId })
  const pageSession: PageSessionEntity = yield call(getPageSession, {
    pageSessionId: pageId,
  })
  const cart: CartEntity = yield call(getCart, pageSession.cartId)

  // store first pickup has special needs
  if (
    cart.fulfillment === "pickup" &&
    pageSession.pageFlow === "store-first" &&
    !pageSession.hasLoadedStores
  ) {
    // pause until a store has been selected
    // or fulfillment has been changed
    yield race([
      take(actions.updatedStoreForPage(page.id).type),
      take(actions.updatedFulfillmentForPage(page.id).type),
    ])
  }

  if (uploadId) {
    yield call(updateLineItemFromUpload, {
      lineItemId,
      uploadId,
    })
  }

  yield put(
    actions.updateOrderSummary({
      reason: "created line item after upload",
      reasonType: "cartChange",
    }),
  )

  return lineItemId
}

export function* watchUploadUriImages(): SagaIterator {
  yield takeEvery(actions.uploadUriImages.type, processUploadUriImages)
}

function* processUploadUriImages(
  action: PayloadAction<UploadUriImagesPayload>,
): SagaIterator {
  const { imageURIs, pageId, toast, scrollTo, nextBlockName } = action.payload

  if (imageURIs.length === 0) {
    return
  }

  const imageURIsWithCount = new Map<string, number>()
  imageURIs.forEach(uri => {
    imageURIsWithCount.set(uri, (imageURIsWithCount.get(uri) ?? 0) + 1)
  })

  let uploadErrorCount = 0

  for (const [imageUri, count] of imageURIsWithCount) {
    try {
      const response = yield call(fetch, imageUri)
      const contentType = response.headers.get("content-type")
      if (
        contentType &&
        UPLOAD_ACCEPTED_FILE_TYPES_IMAGES.includes(contentType)
      ) {
        const blob = yield call([response, "blob"])
        const file = new File([blob], imageUri, { type: contentType })

        for (let i = 0; i < count; i++) {
          yield put(actions.createUpload({ pageId, file }))
        }
      } else {
        throw new Error(`Invalid file type: ${contentType}`)
      }
    } catch (error: any) {
      uploadErrorCount++
    }
  }

  if (imageURIs.length !== uploadErrorCount && scrollTo && nextBlockName) {
    scrollTo(nextBlockName)
  }

  // Show error popup if there are any errors
  if (uploadErrorCount > 0) {
    const title = t(
      "redux.saga.processUploads.processUploadUriImages.ErrorTitle",
      { count: uploadErrorCount },
    )
    const description = t(
      "redux.saga.processUploads.processUploadUriImages.ErrorDescription",
      { count: uploadErrorCount },
    )

    const toastOptions: UseToastOptions = {
      status: "error",
      duration: 3000,
      isClosable: true,
      title,
      description,
    }

    toast(toastOptions)
  }
}
